import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import apiClient from '../../utils/apiClient';
import { useSnackbar } from 'notistack';
import { districtCache } from '../../services/districtCacheService';
import { schoolCache } from '../../services/cacheService';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom icon for drop-a-pin locations
const dropAPinIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to fit map bounds to all markers
const MapBoundsFitter = ({ locations }) => {
  const map = useMap();
  
  useEffect(() => {
    if (locations && locations.length > 0) {
      const validLocations = locations.filter(loc => loc.latitude && loc.longitude);
      if (validLocations.length > 0) {
        const bounds = L.latLngBounds(
          validLocations.map(loc => [loc.latitude, loc.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
  }, [locations, map]);
  
  return null;
};

// Grade definitions (same as NewSchoolWorkflow)
const AVAILABLE_GRADES = [
  { id: 'KG', label: 'Kindergarten', shortLabel: 'K' },
  { id: '01', label: 'Grade 1', shortLabel: '1' },
  { id: '02', label: 'Grade 2', shortLabel: '2' },
  { id: '03', label: 'Grade 3', shortLabel: '3' },
  { id: '04', label: 'Grade 4', shortLabel: '4' },
  { id: '05', label: 'Grade 5', shortLabel: '5' },
  { id: '06', label: 'Grade 6', shortLabel: '6' },
  { id: '07', label: 'Grade 7', shortLabel: '7' },
  { id: '08', label: 'Grade 8', shortLabel: '8' },
  { id: '09', label: 'Grade 9', shortLabel: '9' },
  { id: '10', label: 'Grade 10', shortLabel: '10' },
  { id: '11', label: 'Grade 11', shortLabel: '11' },
  { id: '12', label: 'Grade 12', shortLabel: '12' }
];

// Format grades for display
const formatGrades = (grades) => {
  if (!grades || grades.length === 0) return 'None';
  
  // Map grade codes to display labels
  const gradeLabels = grades.map(g => {
    const grade = AVAILABLE_GRADES.find(ag => ag.id === g);
    return grade ? grade.shortLabel : g;
  });
  
  return gradeLabels.join(', ');
};

// Grade Edit Modal Component
const GradeEditModal = ({ open, onClose, location, onSave }) => {
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open && location) {
      setSelectedGrades(location.grades_offered || []);
    }
  }, [open, location]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiClient.put(`/v2/admin/drop-a-pin-locations/${location.uuid}/grades`, {
        grades: selectedGrades
      });
      
      // Clear district metrics cache since grades changed (affects metrics calculations)
      // We clear the entire cache to ensure all teams get updated metrics
      try {
        console.log('Clearing district cache after grade update...');
        await districtCache.initialize();
        await districtCache.clearAllMetrics();
        console.log('District cache cleared successfully - dashboard will refresh on next visit');
      } catch (cacheErr) {
        console.warn('Failed to clear district cache:', cacheErr);
        // Don't fail the operation if cache clearing fails
      }
      
      // Clear school cache so the updated grade_range appears immediately in other tools
      try {
        console.log('Clearing school cache after grade update...');
        await schoolCache.initialize();
        await schoolCache.clearAllCache();
        console.log('School cache cleared successfully - updated grades will appear in all tools');
      } catch (cacheErr) {
        console.warn('Failed to clear school cache:', cacheErr);
        // Don't fail the operation if cache clearing fails
      }
      
      // Show success message with metrics and AI summary status
      const parts = [];
      if (response.metrics_reprocessed) {
        parts.push('metrics recalculated');
      }
      if (response.ai_summary_regenerated) {
        parts.push('AI summary regenerated');
      }
      if (parts.length === 2) {
        enqueueSnackbar(`Grades updated: ${parts.join(' and ')}. District dashboard will refresh.`, { variant: 'success' });
      } else if (parts.length === 1) {
        enqueueSnackbar(`Grades updated: ${parts[0]}.`, { variant: 'success' });
      } else {
        enqueueSnackbar('Grades updated successfully (metrics and/or AI summary may still be processing)', { variant: 'warning' });
      }
      
      onSave(location.uuid, selectedGrades);
      onClose();
    } catch (err) {
      console.error('Error updating grades:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update grades';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!location) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Grades for {location.name}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Changing grades will automatically recalculate metrics and regenerate AI summaries for this location. This ensures district dashboard data and AI summaries reflect the correct grade ranges.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select all grades that this location offers:
        </Typography>
        <FormGroup row>
          {AVAILABLE_GRADES.map((grade) => (
            <FormControlLabel
              key={grade.id}
              control={
                <Checkbox
                  checked={selectedGrades.includes(grade.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedGrades(prev => [...prev, grade.id]);
                    } else {
                      setSelectedGrades(prev => prev.filter(g => g !== grade.id));
                    }
                  }}
                />
              }
              label={grade.shortLabel}
              sx={{ minWidth: 80 }}
            />
          ))}
        </FormGroup>
        
        {/* Quick select buttons */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedGrades(['KG', '01', '02', '03', '04', '05'])}
          >
            Elementary (K-5)
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedGrades(['06', '07', '08'])}
          >
            Middle (6-8)
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedGrades(['09', '10', '11', '12'])}
          >
            High School (9-12)
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedGrades(AVAILABLE_GRADES.map(g => g.id))}
          >
            All Grades (K-12)
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedGrades([])}
          >
            Clear All
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DropAPinDashboard = () => {
  const [allLocations, setAllLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { enqueueSnackbar} = useSnackbar();
  
  // Filter states
  const [filters, setFilters] = useState({
    team: '',
    city: '',
    state: '',
    startDate: '',
    endDate: ''
  });

  // Grade edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  const fetchDropAPinLocations = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get('/v2/admin/drop-a-pin-locations');
      setAllLocations(response.locations || []);
    } catch (err) {
      console.error('Error fetching drop-a-pin locations:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch drop-a-pin locations';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchDropAPinLocations();
  }, [fetchDropAPinLocations]);
  
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };
  
  const handleClearFilters = () => {
    setFilters({
      team: '',
      city: '',
      state: '',
      startDate: '',
      endDate: ''
    });
  };
  
  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  const handleEditGrades = (location) => {
    setEditingLocation(location);
    setEditModalOpen(true);
  };

  const handleGradesSaved = (uuid, newGrades) => {
    // Update the location in allLocations
    setAllLocations(prev => prev.map(loc => 
      loc.uuid === uuid ? { ...loc, grades_offered: newGrades } : loc
    ));
  };

  // Get unique values for dropdowns (based on all data, not filtered)
  const uniqueTeams = React.useMemo(() => {
    const teams = [...new Set(allLocations.map(loc => loc.team_name).filter(Boolean))];
    return teams.sort();
  }, [allLocations]);

  const uniqueCities = React.useMemo(() => {
    const cities = [...new Set(allLocations.map(loc => loc.city).filter(Boolean))];
    return cities.sort();
  }, [allLocations]);

  const uniqueStates = React.useMemo(() => {
    const states = [...new Set(allLocations.map(loc => loc.state).filter(Boolean))];
    return states.sort();
  }, [allLocations]);

  // Apply client-side filtering
  const locations = React.useMemo(() => {
    return allLocations.filter(location => {
      // Filter by team
      if (filters.team && location.team_name !== filters.team) {
        return false;
      }
      
      // Filter by city
      if (filters.city && location.city !== filters.city) {
        return false;
      }
      
      // Filter by state
      if (filters.state && location.state !== filters.state) {
        return false;
      }
      
      // Filter by start date
      if (filters.startDate && location.created_at) {
        const locationDate = new Date(location.created_at);
        const startDate = new Date(filters.startDate);
        if (locationDate < startDate) {
          return false;
        }
      }
      
      // Filter by end date
      if (filters.endDate && location.created_at) {
        const locationDate = new Date(location.created_at);
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999); // Include the entire end date
        if (locationDate > endDate) {
          return false;
        }
      }
      
      return true;
    });
  }, [allLocations, filters]);

  const validLocations = locations.filter(loc => loc.latitude && loc.longitude);
  const centerLat = validLocations.length > 0 
    ? validLocations.reduce((sum, loc) => sum + loc.latitude, 0) / validLocations.length 
    : 39.8283;
  const centerLng = validLocations.length > 0 
    ? validLocations.reduce((sum, loc) => sum + loc.longitude, 0) / validLocations.length 
    : -98.5795;

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ mb: 3, p: 3, bgcolor: 'background.default' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOnIcon fontSize="large" color="primary" />
          Drop-a-Pin Locations Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage all custom drop-a-pin locations created by your teams.
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <FilterListIcon color="primary" />
          <Typography variant="h6">Filters</Typography>
          {hasActiveFilters && (
            <Chip 
              label="Filters Active" 
              size="small" 
              color="primary" 
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Team"
              value={filters.team}
              onChange={(e) => handleFilterChange('team', e.target.value)}
              size="small"
            >
              <MenuItem value="">All Teams</MenuItem>
              {uniqueTeams.map((team) => (
                <MenuItem key={team} value={team}>
                  {team}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              label="City"
              value={filters.city}
              onChange={(e) => handleFilterChange('city', e.target.value)}
              size="small"
            >
              <MenuItem value="">All Cities</MenuItem>
              {uniqueCities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              label="State"
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
              size="small"
            >
              <MenuItem value="">All States</MenuItem>
              {uniqueStates.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={1}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              size="small"
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <LocationOnIcon fontSize="large" color="primary" />
                <Box>
                  <Typography variant="h4" component="div">
                    {locations.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Drop-a-Pin Locations
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <BusinessIcon fontSize="large" color="secondary" />
                <Box>
                  <Typography variant="h4" component="div">
                    {new Set(locations.map(loc => loc.team_id).filter(Boolean)).size}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Teams Using Drop-a-Pin
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <LocationOnIcon fontSize="large" color="success" />
                <Box>
                  <Typography variant="h4" component="div">
                    {validLocations.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Locations with Coordinates
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Map */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Location Map
        </Typography>
        <Box sx={{ height: 400, position: 'relative' }}>
          {validLocations.length > 0 ? (
            <MapContainer
              center={[centerLat, centerLng]}
              zoom={4}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                url="https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>'
              />
              <MapBoundsFitter locations={validLocations} />
              <MarkerClusterGroup>
                {validLocations.map((location) => (
                  <Marker
                    key={location.uuid}
                    position={[location.latitude, location.longitude]}
                    icon={dropAPinIcon}
                  >
                    <Popup>
                      <Box sx={{ minWidth: 200 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {location.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Team:</strong> {location.team_name}
                        </Typography>
                        {location.city && location.state && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Location:</strong> {location.city}, {location.state}
                          </Typography>
                        )}
                        {location.address && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Address:</strong> {location.address}
                          </Typography>
                        )}
                      </Box>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          ) : (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              height="100%" 
              bgcolor="grey.100"
            >
              <Typography color="text.secondary">
                No locations with valid coordinates to display
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Locations Table */}
      <Paper>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            All Drop-a-Pin Locations
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Team</strong></TableCell>
                <TableCell><strong>City</strong></TableCell>
                <TableCell><strong>State</strong></TableCell>
                <TableCell><strong>Grades Offered</strong></TableCell>
                <TableCell><strong>Address</strong></TableCell>
                <TableCell><strong>Coordinates</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.length > 0 ? (
                locations.map((location) => (
                  <TableRow key={location.uuid} hover>
                    <TableCell>{location.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={location.team_name} 
                        size="small" 
                        color={location.team_name === 'Unassigned' ? 'default' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>{location.city || '-'}</TableCell>
                    <TableCell>{location.state || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatGrades(location.grades_offered)}
                      </Typography>
                    </TableCell>
                    <TableCell>{location.address || '-'}</TableCell>
                    <TableCell>
                      {location.latitude && location.longitude ? (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No coordinates
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {location.created_at ? new Date(location.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleEditGrades(location)}
                        title="Edit Grades"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No drop-a-pin locations found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Grade Edit Modal */}
      <GradeEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        location={editingLocation}
        onSave={handleGradesSaved}
      />
    </Container>
  );
};

export default DropAPinDashboard;

